import { DEFAULT_REDACTED_KEYS, REDACTED, normalizeKey } from './redact.js';
/**
 * Taking the secrets out of a URL before it is written down.
 *
 * Field-name redaction cannot help here, and that is the whole problem: the
 * key is `url`, which is innocent, while the *value* carries the credential.
 * A page URL routinely does — an OAuth implicit-flow `#access_token=…`, a
 * `?code=` on the way back from a provider, a password-reset `?token=`, a
 * pre-signed download link. The browser logger attaches the page URL to every
 * record by default, so without this the library itself would be the thing
 * shipping those to a log group with months of retention.
 *
 * What survives is the part that makes a record useful: origin, path, benign
 * query parameters, and a routing fragment.
 */
/**
 * Parameter names whose values never survive, on top of
 * {@link DEFAULT_REDACTED_KEYS}.
 *
 * Matched as whole names. `code` and `state` are OAuth's, `sig` and
 * `awsaccesskeyid` belong to pre-signed URLs, and `key` is here because it is
 * far more often an API key than anything worth keeping. All of them are
 * overridable — a caller whose `key` means a sort key can say so.
 */
export const DEFAULT_URL_SECRET_PARAMS = [
    'code',
    'state',
    'sig',
    'key',
    'jwt',
    'otp',
    'ticket',
    'session',
    'nonce',
    'awsaccesskeyid',
];
/**
 * Matched anywhere inside a parameter name, not just as the whole of it.
 *
 * Exact matching is not enough in a URL, because the names are vendor-prefixed:
 * a pre-signed S3 link carries `X-Amz-Signature`, `X-Amz-Credential` and
 * `X-Amz-Security-Token`, none of which equals anything a sensible list would
 * contain. Each stem here is long enough that a substring match is a real
 * signal — which is why `sig` and `key` are in the exact list above instead,
 * where they cannot swallow `design` or `monkey`.
 */
export const DEFAULT_URL_SECRET_STEMS = [
    'token',
    'secret',
    'password',
    'passwd',
    'signature',
    'apikey',
    'credential',
    'authorization',
    'sessionid',
    'privatekey',
];
/**
 * Redact a URL's credentials, leaving it recognisable.
 *
 * Anything unparseable is thrown away rather than passed through: a string
 * this cannot reason about is a string it cannot promise anything about, and
 * "[unparseable url]" in a log is a far better outcome than a token.
 */
/**
 * The single answer to "does this name label a credential?".
 *
 * Exported because the same question is asked in three places — a query
 * parameter, a fragment parameter, and a `name=value` pair sitting in prose —
 * and the answer must not differ between them. Redacting `?token=` in a URL
 * while leaving `token=` in an error message is not a policy, it is an accident
 * of where the code happened to look.
 */
export function createSecretNameMatcher(options = {}) {
    const overridden = options.params !== undefined;
    const secrets = new Set((options.params ?? [...DEFAULT_REDACTED_KEYS, ...DEFAULT_URL_SECRET_PARAMS])
        .concat(options.extraParams ?? [])
        .map(normalizeKey));
    // A caller who supplied their own list has taken the decision over; applying
    // the stems anyway would make that list impossible to narrow.
    const stems = overridden ? [] : DEFAULT_URL_SECRET_STEMS.map(normalizeKey);
    return (name) => {
        const normalized = normalizeKey(name);
        return secrets.has(normalized) || stems.some((stem) => normalized.includes(stem));
    };
}
export function redactUrl(url, options = {}) {
    const placeholder = options.placeholder ?? REDACTED;
    const isSecret = createSecretNameMatcher(options);
    // A relative URL is legitimate — a single-page app may report `/reports/12`.
    // Parsing it against a sentinel origin and putting the result back together
    // keeps one code path for both shapes.
    const RELATIVE_BASE = 'https://relative.invalid';
    let parsed;
    let relative = false;
    try {
        parsed = new URL(url);
    }
    catch {
        try {
            parsed = new URL(url, RELATIVE_BASE);
            relative = true;
        }
        catch {
            return '[unparseable url]';
        }
    }
    // `https://user:token@host` — rare, and a credential in the clear when it
    // does happen.
    if (parsed.username !== '' || parsed.password !== '') {
        parsed.username = '';
        parsed.password = '';
    }
    const query = redactParams(parsed.search.replace(/^\?/, ''), isSecret, placeholder);
    if (query !== undefined)
        parsed.search = query === '' ? '' : `?${query}`;
    const fragment = parsed.hash.replace(/^#/, '');
    if (fragment !== '') {
        // A hash route may carry its own query — `#/reports/12?tab=errors`.
        // Splitting it off keeps the route readable instead of percent-encoding
        // the whole thing into one unreadable parameter name.
        const split = fragment.startsWith('/') ? fragment.indexOf('?') : -1;
        const route = split === -1 ? '' : fragment.slice(0, split);
        const rest = split === -1 ? fragment : fragment.slice(split + 1);
        const redacted = redactParams(rest, isSecret, placeholder);
        if (redacted !== undefined) {
            parsed.hash = route === '' ? `#${redacted}` : `#${route}?${redacted}`;
        }
    }
    const text = parsed.toString();
    return relative ? text.slice(RELATIVE_BASE.length) : text;
}
/**
 * Every URL inside a piece of free text, redacted where it stands.
 *
 * This is the one form of secret that can be found reliably in prose, and error
 * text is full of it: `connect ECONNREFUSED postgres://api:hunter2@db:5432`,
 * a fetch failure quoting the URL it called, a browser stack frame naming the
 * bundle it loaded with a signed query string. Key-based redaction cannot help
 * — there is no key — and general secret-hunting in free text is guesswork, so
 * this deliberately does one narrow thing well and leaves the rest of the
 * sentence exactly as written.
 */
export function redactUrlsIn(text, options = {}) {
    // Any scheme, since `postgres://` and `redis://` carry credentials as often
    // as `https://` does. Terminated by whitespace or a bracket/quote, which is
    // as much as can be said about where a URL ends inside a sentence.
    return text.replace(/\b[a-z][a-z0-9+.-]*:\/\/[^\s'"<>()[\]{}]+/gi, (match) => {
        // Sentence punctuation is not part of the URL, and swallowing it would
        // corrupt the surrounding text.
        const trailing = /[.,;:!?]+$/.exec(match)?.[0] ?? '';
        const url = trailing === '' ? match : match.slice(0, -trailing.length);
        return redactUrl(url, options) + trailing;
    });
}
/**
 * Redact one `a=b&c=d` string — a query, or a fragment carrying the same shape.
 *
 * Both halves of a URL go through this, because both have the same two traps
 * and an earlier version of this file fell into both.
 *
 * **Nothing is decided by looking at the raw text.** Telling a token-bearing
 * fragment from a hash route by searching for a literal `=` is a hole:
 * `#access_token%3Dghp_secret` has none, and the token sails past. So the
 * string is always parsed — `URLSearchParams` decodes as it goes, so an encoded
 * separator is seen for what it is — and rewritten only when something actually
 * matched. That is what leaves `#/reports/12` byte for byte identical: it
 * parses to one meaningless key, nothing matches, nothing is rewritten.
 *
 * **The secret can be in the key.** `token%3Dabc123` parses to the single key
 * `token=abc123` with an empty value, so replacing the *value* removes nothing
 * at all — the credential is still sitting in the name. A matched key is
 * therefore re-emitted trimmed at its first separator, encoded or not, which
 * also disposes of the double-encoded case.
 *
 * Returns the replacement, or `undefined` when there was nothing to redact.
 */
function redactParams(source, isSecret, placeholder) {
    if (source === '')
        return undefined;
    const out = new URLSearchParams();
    let touched = false;
    for (const [name, value] of new URLSearchParams(source)) {
        if (isSecret(name)) {
            out.append(secretKeyName(name), placeholder);
            touched = true;
        }
        else {
            out.append(name, value);
        }
    }
    return touched ? out.toString() : undefined;
}
/**
 * The name of a matched parameter, with any value hiding inside it removed.
 *
 * A key containing a separator was an encoded pair that arrived as one token;
 * everything after that separator is the value, and must not survive being
 * re-emitted. Both the decoded and the still-encoded form are cut, so a
 * double-encoded `token%253Dabc` loses its tail too.
 */
function secretKeyName(name) {
    return name.split(/=|%3D/i)[0] ?? name;
}
//# sourceMappingURL=url.js.map